import React, { useState, useRef, useEffect } from "react";
import { Input, TextArea, Checkbox } from "@repo/ui";
import PageContainer from "../../../components/PageContainer/PageContainer";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { colors } from "@repo/ui";
import StepIndicator from "../../../components/StepIndicator/StepIndicator";
import { useAppDispatch, useAppSelector } from "../../../hooks/useAppDispatch";
import { setDedication, setGuestMedia } from "../../../redux/slices/checkoutSlice";
import BackButton from "../../../components/BackButton";
import { RootState } from "../../../redux/store";
import { useTranslation } from "react-i18next";
import { withTranslations } from "../../../hoc/withTranslations/withTranslations";
import { useMediaQuery } from "react-responsive";

// Import our media API services
import {
  generateGuestUploadURL,
  generateGuestGetURL,
  uploadMedia,
  verifyGuestMediaUpload,
} from "../../../services/mediaApiService";
import LoadingButton from "../../../components/LoadingButton/LoadingButton";

const LOCALE = "modules_Guests_pages_DedicationPage";

const DedicationPage: React.FC = () => {
  const { t } = useTranslation(LOCALE);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

  const {
    name: storedName,
    message: storedMessage,
    isAnonymous: storedIsAnonymous,
    mediaID: storedMediaID,
    mediaCapability: storedMediaCapability,
  } = useAppSelector((state: RootState) => state.checkout);

  const { id } = useParams<{ id: string }>();
  const giftTableIDFromState = useAppSelector(
    (state: RootState) => state.guestGiftTable.ID
  );
  const giftTableID = id || giftTableIDFromState;

  const [isAnonymous, setIsAnonymous] = useState(storedIsAnonymous || false);
  const [name, setName] = useState(storedName || "");
  const [message, setMessage] = useState(storedMessage || "");
  const [uploading, setUploading] = useState(false);
  const [mediaUploadProgress, setMediaUploadProgress] = useState(0);
  const [mediaPreviewURL, setMediaPreviewURL] = useState<string | null>(null);
  const [mediaPreviewMimeType, setMediaPreviewMimeType] = useState<
    string | null
  >(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Restore fields on page load or when coming back from preview.
  useEffect(() => {
    if (location.state?.fromPreviewPage) {
      setName(storedName);
      setMessage(storedMessage);
      setIsAnonymous(storedIsAnonymous);
    } else {
      setName("");
      setMessage("");
      setIsAnonymous(false);
      // Only clear preview if there is no stored mediaID.
      if (!storedMediaID) {
        setMediaPreviewURL(null);
        setMediaPreviewMimeType(null);
      }
    }
    // Do not observe storedMediaID changes here.
  }, [location.state, storedName, storedMessage, storedIsAnonymous]);

  // On each page load (mount), if a storedMediaID exists, refresh the preview URL.
  useEffect(() => {
    if (storedMediaID && storedMediaCapability) {
      generateGuestGetURL(storedMediaID, storedMediaCapability)
        .then((getResponse) => {
          console.log("Refreshed getResponse:", getResponse);
          console.log("Refreshed Signed URL:", getResponse.signedURL);
          console.log("Refreshed MIME type:", getResponse.media.mime_type);
          setMediaPreviewURL(getResponse.signedURL);
          setMediaPreviewMimeType(getResponse.media.mime_type);
        })
        .catch((err) =>
          console.error("Error refreshing media preview URL:", err)
        );
    }
  }, []); // Note: storedMediaID is not in dependency array

  const handleMediaUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setMediaUploadProgress(0);
    try {
      // Get file extension and generate a unique file name.
      const extension = file.name.split(".").pop();
      const dateTime = new Date().toISOString().replace(/[:.-]/g, "");
      const fileName = `${dateTime}-dedication.${extension}`;

      // Generate a presigned URL for upload.
      const { presignedURL, mediaID: uploadedMediaID, capability } =
        await generateGuestUploadURL(giftTableID, fileName, file.type);
      setMediaUploadProgress(30);

      // Upload the file.
      await uploadMedia(presignedURL, file);
      setMediaUploadProgress(70);

      // Verify the upload.
      await verifyGuestMediaUpload(uploadedMediaID, capability);
      setMediaUploadProgress(100);

      // Generate a get URL to preview the uploaded media.
      const getResponse = await generateGuestGetURL(uploadedMediaID, capability);
      console.log("getResponse:", getResponse);
      console.log("Signed URL:", getResponse.signedURL);
      console.log("MIME type:", getResponse.media.mime_type);

      setMediaPreviewURL(getResponse.signedURL);
      setMediaPreviewMimeType(getResponse.media.mime_type);

      // Save the media ID in Redux.
      dispatch(setGuestMedia({ mediaID: uploadedMediaID, capability }));
    } catch (err) {
      console.error("Error uploading media:", err);
    } finally {
      setUploading(false);
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleContinue = () => {
    dispatch(
      setDedication({
        name: isAnonymous ? t("anonymous") : name,
        message,
        isAnonymous,
      })
    );
    navigate(`/guests/checkout/preview/${giftTableID}`, {
      state: { fromDedicationPage: true },
    });
  };

  const handleBackToCart = () => {
    navigate(`/guests/gifttable/cart/${giftTableID}`);
  };

  return (
    <PageContainer startColor="white" endColor={colors.background}>
      {!isMobile && (
        <div className="py-4">
          <StepIndicator
            steps={[t("step_message"), t("step_summary"), t("step_payment")]}
            currentStep={1}
            progressPercentage={50}
          />
        </div>
      )}

      <div className="flex justify-center py-16">
        <div className="max-w-lg w-full bg-white p-8 shadow-md rounded-lg">
          <BackButton text={t("goBack")} onClick={handleBackToCart} />

          <h2 className="text-2xl font-bold mb-4">{t("leaveMessageTitle")}</h2>
          <p className="text-gray-600 mb-6">{t("leaveMessageDescription")}</p>

          {!isAnonymous && (
            <div className="mb-4">
              <label className="block text-gray-700 font-semibold mb-2">
                {t("nameLabel")}
              </label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("namePlaceholder")}
                className="w-full"
              />
            </div>
          )}

          <div className="mb-4">
            <label className="block text-gray-700 font-semibold mb-2">
              {t("messageLabel")}
            </label>
            <TextArea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t("messagePlaceholder")}
              className="w-full"
              rows={4}
            />
          </div>

          <div className="mb-4">
            <p className="text-gray-700 mb-2">{t("addMediaPrompt")}</p>
            <input
              type="file"
              accept="image/*,video/*"
              className="hidden"
              ref={fileInputRef}
              onChange={handleMediaUpload}
            />
            <LoadingButton
              label={uploading ? t("uploading") : t("attachMediaButton")}
              onClick={handleButtonClick}
              progress={mediaUploadProgress}
              type="secondary"
              disabled={uploading}
              className="w-full"
            />
            {mediaPreviewURL && (
              <div className="mt-4">
                {mediaPreviewMimeType?.startsWith("image") ? (
                  <img
                    src={mediaPreviewURL}
                    alt="Media Preview"
                    className="w-full object-cover"
                  />
                ) : mediaPreviewMimeType?.startsWith("video") ? (
                  <video src={mediaPreviewURL} controls className="w-full" />
                ) : (
                  <p>{t("mediaPreviewUnavailable")}</p>
                )}
              </div>
            )}
          </div>

          <div className="mb-4 flex items-center">
            <Checkbox
              checked={isAnonymous}
              onChange={() => setIsAnonymous(!isAnonymous)}
              label={t("anonymousCheckbox")}
            />
          </div>

          <LoadingButton
            label={t("continueButton")}
            onClick={handleContinue}
            progress={0} // No progress for continue button
            type="primary"
            className="w-full"
            disabled={false}
          />
        </div>
      </div>
    </PageContainer>
  );
};

export default withTranslations(LOCALE)(DedicationPage);
